<script lang="ts" setup>
  import { Ban, CalendarClock, RotateCcw } from 'lucide-vue-next'
  import { Button } from '~/components/ui/button'
  import { Badge } from '~/components/ui/badge'
  import { CSVIcon, PDFIcon } from '~/components/icons'
  import CircleSpinner from '~/components/CircleSpinner.vue'
  import TooltipedRelativeDate from '~/components/common/TooltipedRelativeDate.vue'
  import GeneratedReportStatusCell from '~/components/reports/GeneratedReportStatusCell.vue'
  import type { ServiceArtefact, ServiceRun, ServiceRunFormat } from '~/types/report-service'
  import { formatISOToDateTime } from '~/utils/date'
  import { formatBytes } from '~/utils/reports/format-bytes'
  import { toReportTitleKeyFromPreset } from '~/utils/reports/service-presets'

  const props = withDefaults(
    defineProps<{
      run: ServiceRun
      downloadingKey?: string | null
      canCancel?: boolean
      canRetry?: boolean
    }>(),
    {
      downloadingKey: null,
      canCancel: false,
      canRetry: false
    }
  )

  const emit = defineEmits<{
    download: [run: ServiceRun, format: ServiceRunFormat]
    cancel: [run: ServiceRun]
    retry: [run: ServiceRun]
  }>()

  const { t, n, locale } = useI18n()

  const ICONS = { csv: CSVIcon, pdf: PDFIcon } as const

  const reportTitle = computed(() => {
    const titleKey = toReportTitleKeyFromPreset(props.run.presetKey)
    return titleKey ? t(titleKey) : (props.run.presetKey ?? '')
  })

  const artefacts = computed<ServiceArtefact[]>(() => props.run.artefacts ?? [])

  const isExpired = (artefact: ServiceArtefact): boolean =>
    !artefact.available || new Date(artefact.expiresAt).getTime() < Date.now()

  const isDownloading = (artefact: ServiceArtefact): boolean =>
    props.downloadingKey === `${props.run.id}:${artefact.format}`

  const iconFor = (format: string): (typeof ICONS)[keyof typeof ICONS] | undefined =>
    format === 'csv' || format === 'pdf' ? ICONS[format] : undefined

  const isDone = computed(() => props.run.status === 'done')
</script>

<template>
  <li class="flex flex-col gap-2 rounded-lg border border-silver-light bg-white p-3">
    <div class="flex items-start justify-between gap-3">
      <div class="flex min-w-0 flex-col">
        <p :title="run.name" class="truncate text-xs+ font-medium text-primary">{{ run.name || reportTitle }}</p>
        <p class="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <template v-if="reportTitle">
            <span>{{ reportTitle }}</span>
            <span aria-hidden="true">·</span>
          </template>
          <TooltipedRelativeDate :date="run.requestedAt" />
          <Badge
            v-if="run.trigger === 'schedule'"
            class="h-4 whitespace-nowrap px-1 py-0 text-tiny font-medium"
            variant="secondary"
          >
            <CalendarClock class="mr-1 size-3" />
            {{ t('reports.downloads.trigger.schedule') }}
          </Badge>
        </p>
      </div>
      <GeneratedReportStatusCell
        :error="run.status === 'failed' ? run.error || t('reports.downloads.internalError') : null"
        :progress="run.progress?.rows"
        :status="run.status"
      />
    </div>

    <div v-if="isDone && artefacts.length > 0" class="flex flex-wrap items-center gap-2">
      <template v-for="artefact in artefacts" :key="artefact.format">
        <Button
          :aria-busy="isDownloading(artefact)"
          :disabled="isExpired(artefact) || !!downloadingKey"
          :title="isExpired(artefact) ? t('reports.downloads.expired') : undefined"
          size="xs"
          variant="outline"
          @click="emit('download', run, artefact.format as ServiceRunFormat)"
        >
          <CircleSpinner v-if="isDownloading(artefact)" class="!size-3" />
          <component :is="iconFor(artefact.format)" v-else-if="iconFor(artefact.format)" class="size-3.5" />
          {{ t('reports.downloads.download', { format: artefact.format.toUpperCase() }) }}
          <span class="font-normal text-muted-foreground">
            {{ formatBytes(artefact.sizeBytes, locale) }} ·
            {{ t('reports.downloads.rows', { count: n(artefact.rowCount) }, artefact.rowCount) }}
          </span>
        </Button>
        <Badge v-if="artefact.truncated" class="h-5 px-1.5 py-0 text-xs font-medium" variant="warning">
          {{ t('reports.downloads.truncated') }}
        </Badge>
      </template>
    </div>

    <div class="flex items-center justify-between gap-2">
      <p v-if="isDone && artefacts[0]" class="text-xs text-muted-foreground">
        {{
          isExpired(artefacts[0])
            ? t('reports.downloads.expired')
            : t('reports.downloads.expiresAt', { date: formatISOToDateTime(artefacts[0].expiresAt) })
        }}
      </p>
      <span v-else />
      <div class="flex items-center gap-1">
        <Button v-if="canRetry" size="xs" variant="ghost" @click="emit('retry', run)">
          <RotateCcw class="size-3.5" />
          {{ t('reports.downloads.retry') }}
        </Button>
        <Button
          v-if="canCancel"
          :aria-label="t('reports.downloads.cancel')"
          size="xs"
          variant="ghost"
          @click="emit('cancel', run)"
        >
          <Ban class="size-3.5" />
          {{ t('reports.downloads.cancel') }}
        </Button>
      </div>
    </div>
  </li>
</template>
